import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Stack,
  TextField,
  InputAdornment,
  Grid,
} from "@mui/material";
import {
  getUsers,
  getDeviceInfo,
  resetDeviceRestriction,
  DeviceInfoResponse,
} from "../../services/core-api";
import {
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { UserRead } from "../../interfaces/core/UserRead";

type DeviceInfo = DeviceInfoResponse;

const DeviceManagement: React.FC = () => {
  const [users, setUsers] = useState<UserRead[]>([]);
  const [deviceInfo, setDeviceInfo] = useState<{ [key: number]: DeviceInfo }>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alertState, setAlertState] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const rowsPerPage = 10;

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      const options = {
        keyword: searchValue,
        page_number: page,
        limit: rowsPerPage,
      };
      const response = await getUsers(options);
      if (response) {
        setUsers(response.users);
        setPage(response.page_number);
        setCount(Math.ceil(response.total / rowsPerPage));

        const deviceInfoMap: { [key: number]: DeviceInfo } = {};
        for (const user of response.users) {
          try {
            const deviceData = await getDeviceInfo(user.id);
            deviceInfoMap[user.id] = deviceData;
          } catch (error) {
            console.error(
              `Failed to fetch device info for user ${user.id}:`,
              error
            );
            deviceInfoMap[user.id] = {
              user_id: user.id,
              username: user.username,
              device_fingerprint: null,
              device_characteristics: null,
              last_login_ip: null,
              last_login_at: null,
            };
          }
        }
        setDeviceInfo(deviceInfoMap);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setAlertState({ type: "error", message: "Failed to fetch users" });
      setUsers([]);
      setPage(1);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, searchValue, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleResetDevice = async (userId: number) => {
    try {
      await resetDeviceRestriction(userId);
      setAlertState({
        type: "success",
        message: "Device restriction reset successfully",
      });

      const deviceData = await getDeviceInfo(userId);
      setDeviceInfo((prev) => ({
        ...prev,
        [userId]: deviceData,
      }));
    } catch (error) {
      console.error("Failed to reset device restriction:", error);
      setAlertState({
        type: "error",
        message: "Failed to reset device restriction",
      });
    }
  };

  const handleViewDetails = (user: UserRead) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    window.scrollTo({
      top: 100,
      left: 100,
      behavior: "smooth",
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getDeviceStatus = (info: DeviceInfo) => {
    if (!info.device_fingerprint) {
      return <Chip label="No Device" color="default" size="small" />;
    }
    return <Chip label="Device Registered" color="success" size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Device Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {alertState && (
        <Alert
          severity={alertState.type}
          onClose={() => setAlertState(null)}
          sx={{ mb: 2 }}
        >
          {alertState.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            id="outlined-basic"
            placeholder="Search Users..."
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: "100%", md: "20rem" },
              float: "left",
              bgcolor: "#fff",
              border: 0,
            }}
            InputProps={{
              sx: {
                fontSize: "13px",
                fontFamily: "Inter",
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: "#d5d5d5 !important", fontSize: "17px" }}
                  />
                </InputAdornment>
              ),
            }}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPage(1);
            }}
          />
        </Grid>
      </Grid>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <TableContainer
          sx={{ maxHeight: 640, border: "1px solid #f1f1f1", p: 2 }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  User
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  Device Status
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  Last Login IP
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  Last Login
                </TableCell>
                <TableCell
                  sx={{ fontFamily: "Inter", fontWeight: 600, pr: 0, pb: 0 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const userDeviceInfo = deviceInfo[user.id];
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {user.name} {user.surname}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          @{user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {userDeviceInfo
                        ? getDeviceStatus(userDeviceInfo)
                        : "Loading..."}
                    </TableCell>
                    <TableCell>
                      {userDeviceInfo?.last_login_ip || "N/A"}
                    </TableCell>
                    <TableCell>
                      {userDeviceInfo
                        ? formatDate(userDeviceInfo.last_login_at)
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Device Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(user)}
                          >
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                        {userDeviceInfo?.device_fingerprint && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleResetDevice(user.id)}
                          >
                            Reset Device
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <Pagination
            count={count}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handlePageChange}
          />
        </Box>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Details - {selectedUser?.name} {selectedUser?.surname}
        </DialogTitle>
        <DialogContent>
          {selectedUser && deviceInfo[selectedUser.id] && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Device Information
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Device Fingerprint
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {deviceInfo[selectedUser.id].device_fingerprint ||
                    "Not registered"}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Login IP
                </Typography>
                <Typography variant="body2">
                  {deviceInfo[selectedUser.id].last_login_ip || "N/A"}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Login Time
                </Typography>
                <Typography variant="body2">
                  {formatDate(deviceInfo[selectedUser.id].last_login_at)}
                </Typography>
              </Box>

              {deviceInfo[selectedUser.id].device_characteristics != null ? (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Device Characteristics
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: "grey.100",
                      p: 2,
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    {JSON.stringify(
                      deviceInfo[selectedUser.id].device_characteristics,
                      null,
                      2
                    )}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {selectedUser &&
            deviceInfo[selectedUser.id]?.device_fingerprint && (
              <Button
                color="warning"
                onClick={() => {
                  handleResetDevice(selectedUser.id);
                  setDialogOpen(false);
                }}
              >
                Reset Device
              </Button>
            )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;
